import {
  Box,
  Modal,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Switch,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CheckCircleIcon, Square3Stack3DIcon, XCircleIcon } from "@heroicons/react/20/solid";
import {
  aggsFilterCategory,
  changeStockStatus,
  FilterProduct,
  getAggStockStatusMenu,
} from "../../../../../config/Services";
import { UserContext } from "App";
import vegIcon from "../../../../../assets/images/veg-new.png";
import nonvegIcon from "../../../../../assets/images/non-veg-new.png";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";

const MenuStockModal = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [filters, setFilters] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingStockIds, setLoadingStockIds] = useState(new Set());
  const [stockStats, setStockStats] = useState({
    totalItems: 0,
    inStockCount: 0,
    outOfStockCount: 0,
  });
  const [totalResults, setTotalResults] = useState(null);
  const showSnackbar = useSnackbar();

  const scrollRef = useRef(null);
  const productsScrollRef = useRef(null);
  const { saleMode } = useContext(UserContext);

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const appConfigData = localStorage.getItem("appConfig");
  const appConfig = appConfigData ? JSON.parse(localStorage.getItem("appConfig")) : {};

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      const payload = {
        page: 1,
        pageSize: 50,
        storeIds: [orgId],
        storeLocations: [locId],
        sortByUpdatedAt: "DESC",
        productTypes: ["MENU"],
        salesChannels: ["DINE_IN", "TAKE_AWAY", "DELIVERY", "SWIGGY"],
      };
      try {
        const response = await aggsFilterCategory(payload);
        const result = await response?.data;

        if (result?.status === "SUCCESS" && result?.data?.data) {
          const totalItems = result?.data?.data?.reduce((sum, cat) => sum + cat.value, 0);
          const categoryFilters = result?.data?.data?.map(
            (category) => `${category?.key} ${category?.value}`
          );

          setFilters([`All ${totalItems}`, ...categoryFilters]);
          setActiveFilter(`All ${totalItems}`);
        }
      } catch (error) {
      } finally {
        setLoadingCategories(false);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open, orgId, locId, saleMode]);

  // Fetch products from API
  const fetchProducts = useCallback(
    async (page = 1, resetItems = false) => {
      setLoadingProducts(true);
      try {
        // Extract category name from activeFilter (remove count)
        const categoryName = activeFilter.split(" ").slice(0, -1).join(" ");
        const isAllCategory = categoryName === "All";

        const payload = {
          page: page,
          pageSize: 20,
          query: searchQuery || "",
          storeLocations: [locId],
          storeIds: [orgId],
          productTypes: ["MENU"],
          salesChannels: ["DINE_IN", "TAKE_AWAY", "DELIVERY"],
          flattenResponse: true,
        };

        // Add category filter if not "All"
        if (!isAllCategory) {
          payload.appCategories = {
            categoryLevel1: [categoryName] || [],
          };
        }

        const response = await FilterProduct(payload);
        const result = await response?.data?.data;

        if (response?.data?.status === "SUCCESS" && result?.data?.data) {
          const newProducts = result?.data?.data || [];
          const formattedProducts = newProducts?.map((product) => {
            const salesChannelData = product?.variant?.externalSalesChannels?.find(
              (channel) => channel.salesChannelName === saleMode
            );

            // Use the stock status from the matched sales channel
            const stockStatus =
              salesChannelData?.stockStatus || product?.variant?.stockStatus || "OUT_OF_STOCK";
            const isInStock = stockStatus === "IN_STOCK";
            return {
              id: `${product?.productId}-${product?.variant?.variantId}-${saleMode}`,
              productId: product?.id,
              variantId: product?.variant?.variantId,
              salesChannel: saleMode,
              name: `${product?.name} ${product?.variant?.name}`,
              ingredients:
                product?.ingredients?.join(", ") || product?.description || "No ingredients listed",
              inStock: isInStock,
              categoryLevel1: product?.categoryLevel1,
              foodType: product?.attributes?.foodType,
            };
          });

          if (resetItems) {
            setItems(formattedProducts);
          } else {
            setItems((prev) => [...prev, ...formattedProducts]);
          }
          setTotalResults(result?.data?.totalRecords);
          setTotalPages(result?.data?.totalPages || 1);
          setCurrentPage(page);
          setHasMore(page < (result?.data?.totalPages || 1));
        }
      } catch (error) {
      } finally {
        setLoadingProducts(false);
      }
    },
    [activeFilter, searchQuery, locId, saleMode]
  );

  // Initial fetch and refetch on filter/search change
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setItems([]);
      fetchProducts(1, true);
    }
  }, [open, activeFilter, searchQuery, fetchProducts]);

  // Add this function in your component, similar to fetchProducts
  const fetchStockStatusAggregation = useCallback(async () => {
    try {
      const categoryName = activeFilter.split(" ").slice(0, -1).join(" ");
      const isAllCategory = categoryName === "All";

      const payload = {
        page: 1,
        pageSize: 1000,
        query: searchQuery || "",
        storeLocations: [locId],
        storeIds: [orgId],
        productTypes: ["MENU"],
        salesChannels: ["DINE_IN", "TAKE_AWAY", "DELIVERY"],
        flattenResponse: true,
      };

      if (!isAllCategory) {
        payload.appCategories = {
          categoryLevel1: [categoryName] || [],
        };
      }

      const response = await getAggStockStatusMenu(payload);

      if (response?.data?.data?.statusCode === 200 && response?.data?.data) {
        const aggregatedData = response?.data?.data?.data;

        // Use reduce for cleaner parsing
        const counts = aggregatedData?.reduce(
          (acc, item) => {
            if (item.key === "IN_STOCK") acc.inStockCount = item.value;
            if (item.key === "OUT_OF_STOCK") acc.outOfStockCount = item.value;
            return acc;
          },
          { inStockCount: 0, outOfStockCount: 0 }
        );

        return {
          totalItems: counts.inStockCount + counts.outOfStockCount,
          inStockCount: counts.inStockCount,
          outOfStockCount: counts.outOfStockCount,
        };
      }

      return { totalItems: 0, inStockCount: 0, outOfStockCount: 0 };
    } catch (error) {
      return { totalItems: 0, inStockCount: 0, outOfStockCount: 0 };
    }
  }, [activeFilter, searchQuery, locId, saleMode]);

  useEffect(() => {
    if (open) {
      fetchStockStatusAggregation().then((stats) => {
        setStockStats(stats);
      });
    }
  }, [open, activeFilter, searchQuery, fetchStockStatusAggregation]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!productsScrollRef.current || loadingProducts || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = productsScrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // Load more when 100px from bottom
      fetchProducts(currentPage + 1, false);
    }
  }, [loadingProducts, hasMore, currentPage, fetchProducts]);

  useEffect(() => {
    const scrollContainer = productsScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleToggleStock = async (id, item) => {
    // Prevent multiple clicks on same item
    if (!appConfig?.enabledFeatures?.ALLOW_MARKING_ITEMS_FROM_KDS) {
      showSnackbar("Marking out of stock feature is disabled in POS configurations", "error");
      return;
    }
    if (loadingStockIds.has(id)) return;

    // Add to loading set
    setLoadingStockIds((prev) => new Set([...prev, id]));

    // Store original state for rollback
    const originalInStock = item.inStock;

    // Optimistically update UI
    setItems((prevItems) =>
      prevItems.map((i) => (i.id === id ? { ...i, inStock: !i.inStock } : i))
    );

    try {
      const payload = {
        productId: item?.productId,
        orgId: orgId,
        locationId: locId,
        variantStockRequests: [
          {
            variantId: item?.variantId,
            salesChannel: ["DINE_IN", "TAKE_AWAY", "DELIVERY", "SWIGGY", "ZOMATO"],
            markAll: false,
          },
        ],
        stockStatus: originalInStock ? "OUT_OF_STOCK" : "IN_STOCK",
        markAll: false,
        updatedBy: retailUserDetails?.uidx,
        updatedByName: retailUserDetails?.firstName,
      };

      const response = await changeStockStatus(payload);

      // Check if API call was successful
      if (response?.data?.status === "SUCCESS") {
        showSnackbar(`Item marked as ${originalInStock ? "Out of Stock" : "In Stock"}`, "success");
      }
    } catch (error) {
      setItems((prevItems) =>
        prevItems.map((i) => (i.id === id ? { ...i, inStock: originalInStock } : i))
      );

      showSnackbar("Failed to update stock status. Please try again.", "error");
    } finally {
      // Remove from loading set
      setLoadingStockIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    setItems([]);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900,
    height: "85vh",
    bgcolor: "#fff",
    border: "1px solid #E6E6E6",
    p: 0,
    borderRadius: "0.75rem",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "1.5rem 1.5rem 0rem 1.5rem",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
            Menu Stock
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CancelIcon sx={{ color: "#999" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: "1.5rem",
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Stats Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 2,
              mb: 3,
            }}
          >
            {/* Total Item Card */}
            <Box
              sx={{
                border: "1px solid #E6E6E6",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                backgroundColor: "#F5F5F5",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}>
                  Total Item
                </Typography>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 600 }}>{totalResults}</Typography>
              </Box>
              <div
                style={{
                  display: "flex",
                  width: "40px",
                  padding: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  border: "1px solid #E9EAEB",
                  background: "#FFF",
                }}
              >
                <Square3Stack3DIcon style={{ width: "20px", height: "20px" }} />
              </div>
            </Box>

            {/* In Stock Card */}
            <Box
              sx={{
                border: "1px solid #E6E6E6",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                backgroundColor: "#F5F5F5",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}>
                  In Stock
                </Typography>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
                  {stockStats?.inStockCount}
                </Typography>
              </Box>
              <div
                style={{
                  display: "flex",
                  width: "40px",
                  padding: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  border: "1px solid #E9EAEB",
                  background: "#FFF",
                }}
              >
                <CheckCircleIcon style={{ width: "20px", height: "20px" }} />
              </div>
            </Box>

            {/* Out Of Stock Card */}
            <Box
              sx={{
                border: "1px solid #E6E6E6",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                backgroundColor: "#F5F5F5",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}>
                  Out Of Stock
                </Typography>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
                  {stockStats?.outOfStockCount}
                </Typography>
              </Box>
              <div
                style={{
                  display: "flex",
                  width: "40px",
                  padding: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  border: "1px solid #E9EAEB",
                  background: "#FFF",
                }}
              >
                <XCircleIcon style={{ width: "20px", height: "20px" }} />
              </div>
            </Box>
          </Box>

          {/* Filter Tabs and Search Bar in One Line */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              alignItems: "center",
            }}
          >
            {/* Left Arrow Button */}
            <IconButton
              onClick={() => scroll("left")}
              size="small"
              sx={{
                bgcolor: "#f5f5f5",
                "&:hover": { bgcolor: "#e0e0e0" },
                flexShrink: 0,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            {/* Scrollable Filter Tabs */}
            <Box
              ref={scrollRef}
              sx={{
                display: "flex",
                gap: 1.5,
                overflowX: "auto",
                flexGrow: 1,
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {loadingCategories ? (
                <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography sx={{ ml: 2, fontSize: "0.875rem", color: "#666" }}>
                    Loading categories...
                  </Typography>
                </Box>
              ) : (
                filters.map((filter) => (
                  <Box
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      transition: "all 0.2s",
                      bgcolor: activeFilter === filter ? "#1976d2" : "transparent",
                      color: activeFilter === filter ? "#fff" : "#666",
                      border: activeFilter === filter ? "none" : "1px solid #E6E6E6",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": {
                        bgcolor: activeFilter === filter ? "#1565c0" : "#f5f5f5",
                      },
                    }}
                  >
                    {filter}
                  </Box>
                ))
              )}
            </Box>

            {/* Right Arrow Button */}
            <IconButton
              onClick={() => scroll("right")}
              size="small"
              sx={{
                bgcolor: "#f5f5f5",
                "&:hover": { bgcolor: "#e0e0e0" },
                flexShrink: 0,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            {/* Search Bar */}
            <TextField
              placeholder="Search items"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                minWidth: "250px",
                flexShrink: 0,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} sx={{ color: "#999" }}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "8px",
                  bgcolor: "#fafafa",
                },
              }}
            />
          </Box>

          {/* Items Grid with Infinite Scroll */}
          <Box
            ref={productsScrollRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: 2,
              }}
            >
              {items?.map((item) => (
                <Box
                  key={item?.id}
                  sx={{
                    border: "1px solid #E6E6E6",
                    borderRadius: "8px",
                    opacity: loadingStockIds.has(item?.id) ? 0.6 : 1,
                    pointerEvents: loadingStockIds.has(item?.id) ? "none" : "auto",
                    transition: "opacity 0.2s",
                  }}
                >
                  <div
                    style={{
                      padding: "10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          width: "20px",
                          height: "20px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={item?.foodType === "VEG" ? vegIcon : nonvegIcon}
                          alt={item?.foodType}
                          style={{ width: "20px", height: "20px" }}
                        />
                      </div>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          flex: 1,
                        }}
                      >
                        {item?.name}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#999",
                        mb: 0.5,
                      }}
                    >
                      Used Items
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#666",
                        mb: 1,
                        lineHeight: 1.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item?.ingredients}
                    </Typography>
                  </div>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: "auto",
                      bgcolor: item.inStock ? "#EDF8FF" : "#FF6B6B",
                      color: item.inStock ? "#0860E6" : "#FFF",
                      borderTop: item.inStock && "1px solid var(--Blue-100, #BFEAFF)",
                      borderRadius: "0px 0px 6px 6px",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: "6px",
                        fontSize: "0.813rem",
                        fontWeight: 600,
                      }}
                    >
                      {loadingStockIds.has(item?.id) ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <>{item?.inStock ? "In Stock" : "Out Of Stock"}</>
                      )}
                    </Box>
                    <Switch
                      checked={item?.inStock}
                      onChange={() => handleToggleStock(item?.id, item)}
                      size="small"
                      disabled={loadingStockIds.has(item?.id)}
                      //   sx={{
                      //     "& .MuiSwitch-switchBase.Mui-checked": {
                      //       color: "#2196F3",
                      //     },
                      //     "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      //       backgroundColor: "#2196F3",
                      //     },
                      //   }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Loading indicator for infinite scroll */}
            {loadingProducts && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={30} />
              </Box>
            )}

            {/* No more items message */}
            {!loadingProducts && !hasMore && items?.length > 0 && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                  No more items to load
                </Typography>
              </Box>
            )}

            {/* No items found */}
            {!loadingProducts && items?.length === 0 && (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography sx={{ fontSize: "1rem", color: "#666" }}>No items found</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MenuStockModal;
